<script setup lang="ts">
/**
 * 个人信息面板（普通注册用户右上角「个人信息」入口）
 *  - 基本信息：姓名 / 单位 / 职务 / 手机号 / 邮箱 / 行业
 *  - 机会帐区域配额：已解锁市级几个、区县几个、还可解锁几个
 *  - 可查看详情的区域：按「一个市一行」展示，行内标注该市下已解锁的区县
 *  - 配额未用满时可继续补选并提交；提交后锁定，需联系咨询顾问获取更多配额才能再改
 *
 * 配额规则（1 市 = N 个区县额度）在后台自动折算，页面不展示具体换算说明。
 */
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { authApi, type QuotaUsage } from '@/api/auth';
import { ApiException } from '@/api/request';
import { useAuthStore } from '@/stores/auth';
import { useRegionStore } from '@/stores/region';
import { toast } from '@/utils/toast';

/** 业务类型选项（与注册表单一致） */
const BIZ_TYPES = ['政府及事业单位', '资产投资', '咨询设计', '产业运营', '其他'];

const props = defineProps<{ show: boolean }>();
const emit = defineEmits<{ (e: 'update:show', v: boolean): void }>();

const auth = useAuthStore();
const region = useRegionStore();
const router = useRouter();

const loading = ref(false);
const saving = ref(false);
const quotaUsage = ref<QuotaUsage>(auth.quotaUsage);
const canConfigure = ref(false);
/** 已授予区域（服务端真值） */
const granted = ref<string[]>([...auth.grantedRegions]);
/** 本次新选中的区域 */
const picked = ref<string[]>([]);

/** 用户类型中文名 */
const userTypeLabel = computed(() => ({
  super: '超级管理员', admin: '管理员', consultant: '咨询顾问', user: '注册用户'
} as Record<string, string>)[auth.userType] || auth.userType);

/** 省级行（所有注册用户可看详情，不占配额） */
const provinceRow = computed(() => ({
  city: '广东省',
  level: '全省 · 省级'
}));

/**
 * 可查看详情的区域：一个市一行
 *  - 行内列出该市下已解锁的区县
 *  - 已解锁市级但未选该市任何区县时，行内提示未选区县
 *  - 区县可跨市选择，因此还需列出「仅区县被解锁」的其他市
 */
const cityRows = computed(() => {
  const cities = region.allRegions.filter(r => r.level === 'city');
  const out: { city: string; counties: string[]; level: string; cityUnlocked: boolean }[] = [];
  for (const c of cities) {
    const countyCodes = region.countiesByCity(c.code);
    const countyNames = region.allRegions
      .filter(r => countyCodes.includes(r.code))
      .map(r => r.name);
    const cityUnlocked = granted.value.includes(c.name);
    const counties = countyNames.filter(n => granted.value.includes(n));
    if (!cityUnlocked && !counties.length) continue;
    out.push({
      city: c.name,
      counties,
      level: countyNames.length ? `${countyNames.length} 个区县可选` : '暂无区县资料',
      cityUnlocked
    });
  }
  return out;
});

/** 本次新选的市 */
const pickedCities = computed(() => picked.value.filter(v => isCityName(v)));
/** 本次新选的区县 */
const pickedCountyNames = computed(() => picked.value.filter(v => !isCityName(v)));

/** 剩余区县额度（已扣除本次新选的区县） */
const remainingCounties = computed(() => Math.max(0, (quotaUsage.value?.remainingCounties ?? 0) - pickedCountyNames.value.length));
/** 剩余市级名额（已扣除本次新选的市） */
const remainingCities = computed(() => Math.max(0, (quotaUsage.value?.remainingCities ?? 0) - pickedCities.value.length));

/** 可选城市（已解锁的市在列表中标注） */
const cityOptions = computed(() => region.allRegions.filter(r => r.level === 'city'));

/** 该市下的区县 */
function countiesOf(cityCode: string) {
  const codes = region.countiesByCity(cityCode);
  return region.allRegions.filter(r => codes.includes(r.code));
}

/** 正在补选的城市（空 = 未展开） */
const pickCity = ref('');

const cityAlreadyGranted = computed(() => pickCity.value && granted.value.includes(pickCity.value));

function isCityName(name: string) {
  return region.allRegions.some(r => r.level === 'city' && r.name === name);
}

/** 切换城市：勾选该市本身即占用 1 个市级名额 */
function onCityChange() {
  if (!pickCity.value) return;
  if (cityAlreadyGranted.value) return;   // 已解锁的市无需重复申请
  if (picked.value.includes(pickCity.value)) return;
  if (remainingCities.value <= 0) {
    toast('已无可用的市级名额，如需解锁更多城市请联系咨询顾问');
    return;
  }
  picked.value.push(pickCity.value);
}

/** 点选区县（占用 1 个区县额度） */
function toggleCounty(name: string) {
  const i = picked.value.indexOf(name);
  if (i >= 0) { picked.value.splice(i, 1); return; }
  // remainingCounties 已扣除本次新选的区县，此处直接判断即可
  if (remainingCounties.value <= 0) {
    toast('已无可用的区县配额，如需解锁更多区县请联系咨询顾问');
    return;
  }
  picked.value.push(name);
}

/** 取消新增某项；取消市级时联动移除该市下本次新选的区县 */
function removePicked(name: string) {
  if (isCityName(name)) {
    const cityRegion = region.allRegions.find(r => r.level === 'city' && r.name === name);
    const countyNames = countiesOf(cityRegion?.code || '').map(c => c.name);
    picked.value = picked.value.filter(v => v === name || !countyNames.includes(v));
    if (cityRegion && pickCity.value === cityRegion.code) pickCity.value = '';
    return;
  }
  picked.value = picked.value.filter(v => v !== name);
}

function reset() {
  picked.value = [];
  pickCity.value = '';
  quotaUsage.value = auth.quotaUsage;
  granted.value = [...auth.grantedRegions];
}

async function load() {
  loading.value = true;
  try {
    const r = await authApi.entitlement();
    quotaUsage.value = r.quotaUsage;
    canConfigure.value = r.canConfigure;
    granted.value = r.grantedRegions;
    await auth.fetchMe();
  } catch (e: unknown) {
    toast(e instanceof Error ? e.message : '加载信息失败');
  } finally {
    loading.value = false;
  }
}

watch(() => props.show, (s) => {
  if (!s) return;
  reset();
  load();
  if (!region.adminLoaded) region.loadAdminDivisions();
  if (!region.manifestLoaded) region.loadManifest();
});

/** 提交补选（新增区域；已选区域不可取消替换） */
async function submit() {
  if (!picked.value.length) { toast('请至少选择 1 个区域'); return; }
  saving.value = true;
  try {
    const r = await authApi.saveRegions(picked.value);
    granted.value = r.grantedRegions;
    quotaUsage.value = r.quotaUsage;
    await auth.fetchMe();
    picked.value = [];
    pickCity.value = '';
    // 是否还能继续补选：按剩余配额重算（配额用满才锁定补选入口）
    const remainCities = r.quotaUsage?.remainingCities ?? 0;
    const remainCounties = r.quotaUsage?.remainingCounties ?? 0;
    canConfigure.value = remainCities > 0 || remainCounties > 0;
    toast(r.trimmed
      ? (r.message || '部分区域超出配额，已按配额保留')
      : (canConfigure.value ? '已新增区域' : '区域配置已提交，配额已用完'));
    emit('update:show', false);
  } catch (e: unknown) {
    toast(e instanceof ApiException ? e.message : (e instanceof Error ? e.message : '提交失败'));
  } finally {
    saving.value = false;
  }
}

// ===== 基本信息编辑（手机号不可改） =====
const editing = ref(false);
const savingProfile = ref(false);
const editForm = ref({ nickname: '', unit: '', title: '', bizType: '', industry: '', email: '' });

function startEdit() {
  const p = auth.profile || {} as Record<string, any>;
  editForm.value = {
    nickname: p.nickname || '',
    unit: p.unit || '',
    title: p.title || '',
    bizType: p.bizType || '',
    industry: p.industry || '',
    email: p.email || ''
  };
  editing.value = true;
}

async function saveProfile() {
  savingProfile.value = true;
  try {
    await authApi.patchProfile({
      nickname: editForm.value.nickname.trim().slice(0, 32),
      unit: editForm.value.unit.trim(),
      title: editForm.value.title.trim(),
      bizType: editForm.value.bizType.trim(),
      industry: editForm.value.industry.trim(),
      email: editForm.value.email.trim().slice(0, 100)
    });
    await auth.fetchMe();
    editing.value = false;
    toast('资料已更新');
  } catch (e: unknown) {
    toast(e instanceof ApiException ? e.message : (e instanceof Error ? e.message : '保存失败'));
  } finally {
    savingProfile.value = false;
  }
}

function goContact() {
  emit('update:show', false);
  router.push('/contact');
}
</script>

<template>
  <div class="modal-mask" :class="{ show }" @click.self="emit('update:show', false)">
    <div class="modal profile-modal">
      <div class="modal-head">
        <h3 class="modal-title">👤 个人信息</h3>
        <button class="modal-close" @click="emit('update:show', false)">✕</button>
      </div>

      <div class="modal-body">
        <p v-if="loading" class="loading-tip">加载中…</p>

        <template v-else>
          <!-- 空闲配额提醒 -->
          <div v-if="!auth.hasAllRegions && remainingCounties > 0" class="pf-remind">
            <span class="pf-remind-icon">🔔</span>
            <span>
              您有 <b>{{ remainingCounties }}</b> 个空余的机会帐资料配额，进入设置区域可解锁更多市 / 区县详情。
            </span>
          </div>

          <!-- 基本信息 -->
          <section class="pf-sec">
            <div class="pf-sec-title pf-sec-title-row">
              <span>基本信息</span>
              <button v-if="!editing" class="pf-edit-btn" @click="startEdit">✎ 更改信息</button>
            </div>

            <!-- 查看态 -->
            <div v-if="!editing" class="pf-info-grid">
              <div class="pf-info-row"><span>姓名 / 昵称</span><b>{{ auth.profile?.nickname || '-' }}</b></div>
              <div class="pf-info-row"><span>手机号</span><b class="mono">{{ auth.phoneMasked }}</b></div>
              <div class="pf-info-row"><span>单位</span><b>{{ auth.profile?.unit || '-' }}</b></div>
              <div class="pf-info-row"><span>职务</span><b>{{ auth.profile?.title || '-' }}</b></div>
              <div class="pf-info-row"><span>业务类型</span><b>{{ auth.profile?.bizType || '-' }}</b></div>
              <div class="pf-info-row"><span>行业</span><b>{{ auth.profile?.industry || '-' }}</b></div>
              <div class="pf-info-row"><span>邮箱</span><b>{{ auth.profile?.email || '-' }}</b></div>
              <div class="pf-info-row"><span>账号类型</span><b>{{ userTypeLabel }}</b></div>
            </div>

            <!-- 编辑态（手机号不可改） -->
            <form v-else class="pf-edit-form" @submit.prevent="saveProfile">
              <div class="pf-edit-grid">
                <label class="pf-field"><span>姓名 / 昵称</span><input v-model="editForm.nickname" class="form-input" maxlength="32" placeholder="选填"></label>
                <label class="pf-field"><span>单位</span><input v-model="editForm.unit" class="form-input" maxlength="100"></label>
                <label class="pf-field"><span>职务</span><input v-model="editForm.title" class="form-input" maxlength="50"></label>
                <label class="pf-field"><span>业务类型</span>
                  <select v-model="editForm.bizType" class="form-input">
                    <option value="">请选择业务类型</option>
                    <option v-for="b in BIZ_TYPES" :key="b" :value="b">{{ b }}</option>
                  </select>
                </label>
                <label class="pf-field"><span>行业</span><input v-model="editForm.industry" class="form-input" maxlength="50"></label>
                <label class="pf-field"><span>邮箱</span><input v-model="editForm.email" class="form-input" type="email" maxlength="100"></label>
              </div>
              <p class="pf-hint">手机号不可修改；如需更换手机号请联系咨询顾问。</p>
              <div class="pf-actions">
                <button type="button" class="btn btn-ghost" :disabled="savingProfile" @click="editing = false">取消</button>
                <button type="submit" class="btn btn-primary" :disabled="savingProfile">{{ savingProfile ? '保存中…' : '保存' }}</button>
              </div>
            </form>
          </section>

          <!-- 区域配额 -->
          <section class="pf-sec">
            <div class="pf-sec-title">机会帐区域配额</div>
            <div class="pf-quota">
              <div class="pf-quota-item">
                <span>已解锁城市</span>
                <b>{{ auth.hasAllRegions ? '全部' : quotaUsage.cityCount + ' 个' }}</b>
              </div>
              <div class="pf-quota-item">
                <span>已解锁区县</span>
                <b>{{ auth.hasAllRegions ? '全部' : quotaUsage.countyCount + ' 个' }}</b>
              </div>
              <div class="pf-quota-item">
                <span>可解锁城市</span>
                <b>{{ auth.hasAllRegions ? '-' : remainingCities + ' 个' }}</b>
              </div>
              <div class="pf-quota-item">
                <span>可解锁区县</span>
                <b :class="{ 'is-warn': !auth.hasAllRegions && remainingCounties <= 0 }">
                  {{ auth.hasAllRegions ? '-' : remainingCounties + ' 个' }}
                </b>
              </div>
            </div>
            <p class="pf-hint">
              省级机会帐所有注册用户均可查看，不占用配额。市级与区县级配额由系统自动折算，
              您可自行选择解锁哪些城市与区县；配置提交后即锁定，如需调整请联系咨询顾问。
            </p>
          </section>

          <!-- 可查看详情的区域 -->
          <section class="pf-sec">
            <div class="pf-sec-title">可查看详情的区域</div>

            <!-- 省级 -->
            <div class="pf-region-row">
              <div class="pf-region-head">
                <span class="pf-region-city">{{ provinceRow.city }}</span>
                <span class="pf-tag pf-tag-prov">省级 · 所有用户可看</span>
              </div>
              <div class="pf-region-sub">全省资源底盘、规模信号与资产化要求（不占配额）</div>
            </div>

            <!-- 市级 + 区县 -->
            <div v-for="r in cityRows" :key="r.city" class="pf-region-row">
              <div class="pf-region-head">
                <span class="pf-region-city">{{ r.city }}</span>
                <span v-if="r.cityUnlocked" class="pf-tag pf-tag-city">市级已解锁</span>
                <span v-else class="pf-tag pf-tag-county">仅区县</span>
                <span class="pf-region-meta">{{ r.level }}</span>
              </div>
              <div v-if="r.counties.length" class="pf-county-list">
                <span v-for="c in r.counties" :key="c" class="pf-chip">{{ c }}</span>
              </div>
              <div v-else class="pf-region-sub">该市暂未选择区县（区县配额可自行分配到其他市）</div>
            </div>

            <p v-if="!cityRows.length" class="pf-empty">
              {{ auth.hasAllRegions ? '您拥有全部区域的机会帐查看权限' : '暂未解锁市级 / 区县机会帐资料' }}
            </p>

            <div v-if="!auth.hasAllRegions" class="pf-lock-note" :class="{ 'is-locked': auth.locked }">
              <span class="pf-lock-icon">{{ auth.locked ? '🔒' : '🔓' }}</span>
              <span v-if="auth.locked">
                已选区域已锁定，<b>不可取消或替换</b>；但配额尚有余额时仍可继续<b>补选更多</b>区域。
              </span>
              <span v-else>
                区域配置尚未锁定，可继续补选；一旦提交即锁定，已选区域不可取消或替换。
              </span>
            </div>
          </section>

          <!-- 补选区域 -->
          <section v-if="!auth.hasAllRegions" class="pf-sec">
            <div class="pf-sec-title">补选区域</div>

            <template v-if="canConfigure">
              <p class="pf-hint">
                可解锁：{{ remainingCities }} / {{ quotaUsage?.cityCapacity ?? 0 }} 个城市 · {{ remainingCounties }} / {{ quotaUsage?.countyCapacity ?? 0 }} 个区县
                <template v-if="auth.locked">（已选区域不可取消，仅可新增）</template>
              </p>
              <div class="pf-pick-row">
                <select v-model="pickCity" class="form-input" @change="onCityChange">
                  <option value="">请选择要解锁的城市</option>
                  <option v-for="c in cityOptions" :key="c.code" :value="c.code"
                          :disabled="!granted.includes(c.name) && !picked.includes(c.name) && remainingCities <= 0">
                    {{ c.name }}{{ granted.includes(c.name) ? '（已解锁）' : (picked.includes(c.name) ? '（已新增）' : (remainingCities <= 0 ? '（市级名额已满）' : '')) }}
                  </option>
                </select>
              </div>
              <div v-if="pickCity" class="pf-pick-cities">
                <div class="pf-hint">
                  <template v-if="cityAlreadyGranted">
                    该市市级已解锁，可直接点选其下区县（每个区县 1 个配额）。
                  </template>
                  <template v-else>
                    已新增市级「{{ cityOptions.find(c => c.code === pickCity)?.name || pickCity }}」；可再点选其下区县（每个区县 1 个配额）。
                  </template>
                </div>
                <div class="pf-county-list">
                  <button v-for="c in countiesOf(pickCity)" :key="c.code"
                          class="pf-chip pf-chip-btn"
                          :class="{ active: picked.includes(c.name), disabled: !picked.includes(c.name) && remainingCounties <= 0 }"
                          @click="toggleCounty(c.name)">{{ c.name }}</button>
                </div>
                <p v-if="!countiesOf(pickCity).length" class="pf-empty">该市下暂无可查看的区县机会帐资料</p>
              </div>

              <div v-if="picked.length" class="pf-picked">
                <div class="pf-hint">本次新增：</div>
                <div class="pf-picked-row">
                  <span
                    v-for="v in picked" :key="v"
                    class="pf-chip pf-chip-btn"
                    :class="{ active: isCityName(v) }"
                    @click="removePicked(v)"
                  >{{ v }}{{ isCityName(v) ? '（市级）' : '' }} ✕</span>
                </div>
              </div>

              <div class="pf-actions">
                <button class="btn btn-ghost" :disabled="saving" @click="emit('update:show', false)">取消</button>
                <button class="btn btn-primary" :disabled="saving || !picked.length" @click="submit">
                  {{ saving ? '提交中…' : (auth.locked ? '提交新增区域' : '提交并锁定') }}
                </button>
              </div>
              <p class="pf-hint pf-hint-warn">
                <template v-if="auth.locked">
                  已选区域不可取消或替换；本次提交仅新增区域。如需获得更多配额，请联系咨询顾问。
                </template>
                <template v-else>
                  提交后即锁定，已选区域不可取消或替换；如需获得更多配额，请联系咨询顾问。
                </template>
              </p>
            </template>

            <div v-else class="pf-blocked">
              <p class="pf-empty">
                您的区域配额已使用完毕，如需解锁更多区域，请联系咨询顾问获取更多配额。
              </p>
              <div class="pf-actions">
                <button class="btn btn-primary" @click="goContact">联系我们咨询顾问</button>
              </div>
            </div>
          </section>

          <section v-else class="pf-sec">
            <div class="pf-hint">您拥有全部区域的机会帐查看权限，无需配置。</div>
          </section>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.profile-modal { width: 620px; max-width: calc(100vw - 32px); }
.profile-modal .modal-body { max-height: calc(100vh - 140px); overflow-y: auto; }

/* 空闲配额提醒 */
.pf-remind {
  display: flex; align-items: flex-start; gap: 8px;
  font-size: 12.5px; line-height: 1.75;
  padding: 10px 12px; border-radius: 8px; margin-bottom: 18px;
  background: #fdf6ec; border: 1px solid #f3ddc4; color: #8a5a1c;
}
.pf-remind b { color: #c96f1a; }
.pf-remind-icon { flex-shrink: 0; }

.pf-sec { margin-bottom: 20px; }
.pf-sec-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--brand-dark, #123527);
  padding-bottom: 6px;
  margin-bottom: 10px;
  border-bottom: 1px solid #e8eeeb;
}
.pf-sec-title-row {
  display: flex; align-items: center; justify-content: space-between;
}
.pf-edit-btn {
  font-family: inherit; font-size: 12px; cursor: pointer;
  padding: 3px 10px; border-radius: 999px;
  background: #fff; color: var(--brand, #1c4d3a);
  border: 1px solid var(--brand, #1c4d3a);
  transition: all .15s;
}
.pf-edit-btn:hover { background: var(--brand, #1c4d3a); color: #fff; }

/* 基本信息编辑态 */
.pf-edit-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 16px; }
.pf-field { display: flex; flex-direction: column; gap: 4px; }
.pf-field span { font-size: 12px; color: var(--text-sub, #556664); }
.pf-field .form-input { font-size: 12.5px; padding: 6px 10px; }

/* 基本信息 */
.pf-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 20px; }
.pf-info-row {
  display: flex; justify-content: space-between; gap: 10px;
  font-size: 12.5px; color: var(--text-sub, #556664); line-height: 1.9;
}
.pf-info-row b { color: var(--text-main, #2c3a38); font-weight: 600; text-align: right; }
.pf-info-row .mono { font-family: ui-monospace, Menlo, Consolas, monospace; }

/* 配额 */
.pf-quota {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px;
  background: #f7faf8; border: 1px solid #e4ece8; border-radius: 8px;
  padding: 12px 14px; margin-bottom: 10px;
}
.pf-quota-item { display: flex; flex-direction: column; gap: 4px; }
.pf-quota-item span { font-size: 11.5px; color: var(--text-weak, #8a9a97); }
.pf-quota-item b { font-size: 16px; color: var(--brand-dark, #123527); font-weight: 700; }
.pf-quota-item b.is-warn { color: var(--hot, #c93d77); }

.pf-hint {
  font-size: 12px; color: var(--text-weak, #8a9a97);
  line-height: 1.75; margin: 0 0 8px;
}
.pf-hint b { color: var(--brand-dark, #123527); }
.pf-hint-warn { color: var(--hot, #c93d77); margin: 8px 0 0; }

/* 区域列表 */
.pf-region-row {
  padding: 10px 12px;
  border: 1px solid #e8eeeb;
  border-radius: 8px;
  margin-bottom: 8px;
  background: #fff;
}
.pf-region-head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.pf-region-city { font-size: 13.5px; font-weight: 700; color: var(--text-main, #2c3a38); }
.pf-region-meta { font-size: 11.5px; color: var(--text-weak, #8a9a97); }
.pf-region-sub { font-size: 12px; color: var(--text-weak, #8a9a97); margin-top: 4px; line-height: 1.7; }

.pf-tag {
  font-size: 11px; padding: 2px 8px; border-radius: 999px;
  border: 1px solid transparent;
}
.pf-tag-prov { background: #e8f4ef; color: #0b7a6a; border-color: #cfe6de; }
.pf-tag-city { background: #fdf1e6; color: #c96f1a; border-color: #f3ddc4; }
.pf-tag-county { background: #eef2f7; color: #5a6b7d; border-color: #dde4ec; }

.pf-county-list { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.pf-chip {
  font-size: 12px; padding: 3px 10px; border-radius: 999px;
  background: #f3f7f5; color: var(--text-sub, #556664); border: 1px solid #e4ece8;
}
.pf-chip-btn { cursor: pointer; font-family: inherit; transition: all .15s; }
.pf-chip-btn:hover { border-color: var(--brand, #1c4d3a); color: var(--brand-dark, #123527); }
.pf-chip-btn.active {
  background: var(--brand, #1c4d3a); color: #fff; border-color: var(--brand, #1c4d3a);
}
.pf-chip-btn.disabled {
  opacity: .42; cursor: not-allowed; filter: grayscale(.6);
}
.pf-chip-btn.disabled:hover { border-color: #e4ece8; color: var(--text-sub, #556664); }

.pf-empty { font-size: 12px; color: var(--text-weak, #8a9a97); margin: 6px 0; }

.pf-lock-note {
  display: flex; align-items: flex-start; gap: 8px;
  font-size: 12px; line-height: 1.75;
  padding: 9px 12px; border-radius: 8px;
  background: #f7faf8; border: 1px solid #e4ece8;
  color: var(--text-sub, #556664);
  margin-top: 10px;
}
.pf-lock-note.is-locked { background: #fdf6ec; border-color: #f3ddc4; color: #8a5a1c; }
.pf-lock-icon { flex-shrink: 0; }

/* 补选 */
.pf-pick-row { margin-bottom: 10px; }
.pf-pick-cities { margin-bottom: 12px; }
.pf-picked { margin-bottom: 10px; }
.pf-picked-row {
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
  padding: 8px 10px; border-radius: 6px; background: #f7faf8;
  border: 1px dashed #dfe8e4; margin-bottom: 6px;
}
.pf-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
.pf-blocked {
  padding: 12px; border-radius: 8px; background: #fdf6ec; border: 1px solid #f3ddc4;
}
</style>
